import { Alert, Button, Card, List, rem, ScrollArea, SimpleGrid, Text, ThemeIcon, Title } from "@mantine/core";
import { useTRPC } from "../trpc";
import { IconAlertCircle, IconCircleCheck } from "@tabler/icons-react";
import { Loader } from "./Loader";
import { groupBy, isEmpty } from "es-toolkit/compat";
import { useCallback, type FC } from "react";
import { FormattedMessage } from "react-intl";
import { ProductFeatures } from "./SubscriptionsFeatures";
import { ProductDescription } from "./SubscriptionsDescription";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsDeveloperPartner, useIsServiceAccount } from "../hooks/user";
import { isFeatureEnabled } from "../utils/posthog";

interface Tier {
  upTo?: number;
  unitAmount?: number;
}

const FlatPrice: FC<{ interval: string; value: number }> = ({ interval, value }) => {
  const decimalUnitAmount = value / 100;
  return (
    <Card.Section inheritPadding pt="md">
      <Title size="h2">
        {interval === "year" && (
          <FormattedMessage
            id="title.flatPriceYear"
            defaultMessage="${decimalUnitAmount} / year"
            values={{ decimalUnitAmount, interval }}
          />
        )}
        {interval === "month" && (
          <FormattedMessage
            id="title.flatPriceMonth"
            defaultMessage="${decimalUnitAmount} / month"
            values={{ decimalUnitAmount, interval }}
          />
        )}
      </Title>
    </Card.Section>
  );
};

const MeteredPrice: FC<{ interval: string; unitAmount: number; meterUnit: string }> = ({ unitAmount, interval, meterUnit }) => {
  const decimalUnitAmount = unitAmount / 100;
  return (
    <Card.Section inheritPadding>
      <Title size="h5">
        <FormattedMessage
          id="title.meteredPrice"
          defaultMessage="${decimalUnitAmount} per {meterUnit} / {interval}"
          values={{ meterUnit, decimalUnitAmount, interval }}
        />
      </Title>
    </Card.Section>
  );
};

const TieredPrice: FC<{ interval: string; currency: string; meterUnit: string; tiers: Tier[] }> = ({ tiers, interval, meterUnit }) => {
  return (
    <Card.Section inheritPadding>
      {tiers
        .map((next, index) => {
          const prevUpTo = tiers[index - 1]?.upTo ?? 0;
          const nextUpTo = next.upTo;
          const unitAmount = next.unitAmount ?? 0;

          return nextUpTo
            ? (
                <Title size="h5" key={index}>
                  {`Between ${prevUpTo} ${meterUnit} to ${nextUpTo} ${meterUnit} $${unitAmount / 100} per ${meterUnit} / ${interval}`}
                </Title>
              )
            : (
                <Title size="h5" key={index}>
                  {`From ${prevUpTo} ${meterUnit}  $${unitAmount / 100} per ${meterUnit} / ${interval}`}
                </Title>
              );
        })}
    </Card.Section>
  );
};

export const AddonSelection: FC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const isServiceAccount = useIsServiceAccount();
  const isDeveloperPartner = useIsDeveloperPartner();

  const {
    data: allProducts = [],
    isLoading: isLoadingProducts,
  } = useQuery(trpc.getProducts.queryOptions());
  const {
    data: subscriptions = [],
    isLoading: isLoadingSubscriptions,
  } = useQuery(trpc.getCurrentSubscriptions.queryOptions());

  const {
    mutateAsync: addProductToSubscription,
    isPending: isPendingCreateSubscription,
  } = useMutation(trpc.addProductToSubscription.mutationOptions());

  const {
    mutateAsync: cancelProductSubscription,
    isPending: isPendingCancelSubscription,
  } = useMutation(trpc.cancelProduct.mutationOptions());

  const {
    mutateAsync: changeSubscription,
    isPending: isPendingChangeSubscription,
  } = useMutation(trpc.changeSubscription.mutationOptions());

  const handleAddProductToSubscription = useCallback((productId: string) => {
    void addProductToSubscription({
      productId,
    }).then(() => {
      void queryClient.invalidateQueries(trpc.getCurrentSubscriptions.queryFilter());
      void queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter());
    });
  }, [addProductToSubscription, queryClient]);

  const handleCancelProduct = useCallback((productId: string) => {
    void cancelProductSubscription({
      productId,
    }).then(() => {
      void queryClient.invalidateQueries(trpc.getCurrentSubscriptions.queryFilter());
      void queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter());
    });
  }, [cancelProductSubscription, queryClient]);

  const handleChangeSubscribe = useCallback((subscriptionId: string, prevProductId: string, nextProductId: string) => {
    void changeSubscription({
      subscriptionId,
      prevProductId,
      nextProductId,
    }).then(() => {
      void queryClient.invalidateQueries(trpc.getCurrentSubscriptions.queryFilter());
      void queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter());
    });
  }, [changeSubscription, queryClient]);

  if (isLoadingProducts || isLoadingSubscriptions) {
    return <Loader />;
  }

  const groupedProducts = groupBy(allProducts, ({ product }) => product.metadata.group);
  const isSubscriptionAddonsEnabled = isFeatureEnabled("enable-subscription-addons");
  const currentSubscription = subscriptions.find(s => s.status === "active");

  return (
    <ScrollArea>
      {isServiceAccount && (
        <Alert
          variant="light"
          icon={<IconAlertCircle size={24} />}
          title="Account is a service account"
          color="yellow"
        >
          <Text size="sm">
            <FormattedMessage
              id="text.serviceAccount"
              defaultMessage="Service accounts do not have access to subscription plans."
            />
          </Text>
        </Alert>
      )}
      {!currentSubscription && (
        <Alert
          variant="light"
          icon={<IconAlertCircle size={24} />}
          title="No active subscription"
          color="yellow"
        >
          <Text size="sm">
            <FormattedMessage
              id="text.noActiveSubscription"
              defaultMessage="You don't have an active payment method. Please add a payment method to continue."
            />
          </Text>
        </Alert>
      )}
      {isDeveloperPartner && (
        <Alert
          variant="light"
          icon={<IconAlertCircle size={24} />}
          title="Account is a developer partner"
          color="yellow"
        >
          <Text size="sm">
            <FormattedMessage
              id="text.developerPartner"
              defaultMessage="Once you upgrade to a paid account, you will no longer get free usage for developer partners."
            />
          </Text>
        </Alert>
      )}
      {!isSubscriptionAddonsEnabled && (
        <Alert
          variant="light"
          icon={<IconAlertCircle size={24} />}
          title="Subscriptions are not enabled"
          color="yellow"
        >
          <Text size="sm">
            <FormattedMessage
              id="text.subscriptionsNotEnabled"
              defaultMessage="Subscriptions are temporarily not enabled. Please try again later."
            />
          </Text>
        </Alert>
      )}

      {(!isServiceAccount && isSubscriptionAddonsEnabled && currentSubscription) &&
        (Object.entries(groupedProducts)
          .filter(([group]) => group === "NoAttribution" || group === "Support" || group === "DataCatalog")
          .map(([group, groupProducts]) => {
            if (isEmpty(groupProducts)) {
              return null;
            }
            return (
              <SimpleGrid key={group} cols={{ base: 1, sm: 2, lg: 3 }} verticalSpacing="md" pt="md">
                {groupProducts
                  .toSorted((a, b) => {
                    return a.product.metadata.order - b.product.metadata.order;
                  })
                  .map((plan) => {
                    const {
                      product: {
                        id: productId,
                        description,
                        name: productName,
                        marketingFeatures,
                        metadata: {
                          order,
                        },
                      },
                      prices,
                    } = plan;

                    const flatPrices = prices
                      .filter(price => price.recurring?.usageType === "licensed");
                    const meteredTieredPrices = prices
                      .filter(price => price.recurring?.usageType === "metered" && price.tiers);
                    const meteredPrices = prices
                      .filter(price => price.recurring?.usageType === "metered" && !price.tiers);

                    const currentGroupSubscriptionProduct = groupProducts
                      .find(product => currentSubscription.prices.some(price => price.productId === product.product.id));

                    const isProductInCurrentSubscription = currentSubscription.prices.some(price => price.productId === productId);

                    const hasGroupProduct = Boolean(currentGroupSubscriptionProduct);

                    const isUpgradeProduct = !isProductInCurrentSubscription && hasGroupProduct && order > (currentGroupSubscriptionProduct?.product.metadata.order ?? 0);
                    const isDowngradeProduct = !isProductInCurrentSubscription && hasGroupProduct && order < (currentGroupSubscriptionProduct?.product.metadata.order ?? 0);

                    return (
                      <Card shadow="sm" padding="xl" radius="md" withBorder key={productId} flex={1}>
                        <Card.Section inheritPadding>
                          <Title size="h3">{productName}</Title>
                        </Card.Section>

                        <Card.Section inheritPadding>
                          <Title size="h4">
                            <ProductDescription productId={productId} description={description} />
                          </Title>
                        </Card.Section>

                        {flatPrices
                          .map((price) => {
                            if (!price.recurring || !price.unitAmount) {
                              return null;
                            }
                            const {
                              id: priceId,
                              unitAmount,
                              recurring: {
                                interval,
                              },
                            } = price;

                            return (
                              <FlatPrice
                                key={priceId}
                                interval={interval}
                                value={unitAmount}
                              />
                            );
                          })}

                        {meteredTieredPrices
                          .map((price) => {
                            if (!price.recurring || !price.tiers || !price.metadata.unitLabel) {
                              return null;
                            }
                            const {
                              id: priceId,
                              currency,
                              tiers,
                              recurring: {
                                interval,
                              },
                              metadata: {
                                unitLabel,
                              },
                            } = price;

                            return (
                              <TieredPrice
                                key={priceId}
                                interval={interval}
                                currency={currency}
                                tiers={tiers}
                                meterUnit={unitLabel}
                              />
                            );
                          })}

                        {meteredPrices
                          .map((price) => {
                            if (!price.recurring || !price.unitAmount || !price.metadata.unitLabel) {
                              return null;
                            }
                            const {
                              id: priceId,
                              unitAmount,
                              recurring: {
                                interval,
                              },
                              metadata: {
                                unitLabel,
                              },
                            } = price;

                            return (
                              <MeteredPrice
                                key={priceId}
                                interval={interval}
                                meterUnit={unitLabel}
                                unitAmount={unitAmount}
                              />
                            );
                          })}

                        <Card.Section inheritPadding>
                          <List
                            spacing="xs"
                            size="sm"
                            center
                            icon={(
                              <ThemeIcon color="teal" size={24} radius="xl">
                                <IconCircleCheck
                                  style={{
                                    width: rem(16),
                                    height: rem(16),
                                  }}
                                />
                              </ThemeIcon>
                            )}
                          >
                            <ProductFeatures productId={productId} features={marketingFeatures} />
                          </List>
                        </Card.Section>

                        <Card.Section mt="auto" p="md">
                          {isProductInCurrentSubscription && (
                            <Button
                              type="button"
                              variant="danger"
                              loading={isPendingCreateSubscription || isPendingCancelSubscription}
                              fullWidth
                              onClick={() => {
                                handleCancelProduct(productId);
                              }}
                            >
                              <FormattedMessage id="button.current" defaultMessage="Cancel subscription" />
                            </Button>
                          )}
                          {!hasGroupProduct && (
                            <Button
                              type="button"
                              variant="primary"
                              loading={isPendingCreateSubscription || isPendingCancelSubscription}
                              fullWidth
                              onClick={() => {
                                handleAddProductToSubscription(productId);
                              }}
                            >
                              <FormattedMessage id="button.subscribe" defaultMessage="Subscribe" />
                            </Button>
                          )}

                          {isUpgradeProduct && (
                            <Button
                              type="button"
                              variant="primary"
                              loading={isPendingChangeSubscription || isPendingCreateSubscription}
                              fullWidth
                              onClick={() => {
                                if (currentGroupSubscriptionProduct) {
                                  handleChangeSubscribe(
                                    currentSubscription.subscriptionId,
                                    currentGroupSubscriptionProduct.product.id,
                                    productId,
                                  );
                                }
                              }}
                            >
                              <FormattedMessage id="button.Upgrade" defaultMessage="Upgrade" />
                            </Button>
                          )}

                          {isDowngradeProduct && (
                            <Button
                              type="button"
                              variant="primary"
                              loading={isPendingChangeSubscription || isPendingCreateSubscription}
                              fullWidth
                              onClick={() => {
                                if (currentGroupSubscriptionProduct) {
                                  handleChangeSubscribe(
                                    currentSubscription.subscriptionId,
                                    currentGroupSubscriptionProduct.product.id,
                                    productId,
                                  );
                                }
                              }}
                            >
                              <FormattedMessage id="button.downgrade" defaultMessage="Downgrade" />
                            </Button>
                          )}
                        </Card.Section>
                      </Card>
                    );
                  })}
              </SimpleGrid>

            );
          }))}
    </ScrollArea>
  );
};
